import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { JOBCARD_STATUSES, STATUS_LABELS, type JobcardStatus } from '@garage/shared'

type DashboardStatusFilter = 'open' | JobcardStatus | 'all'
import { api } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { useFormatters, useFeatures } from '@/lib/hooks.js'
import { Button, PageHeader, EmptyState, Skeleton, DataTable, Segmented, type Column } from '@/ui'
import { StatCard } from '@/components/StatCard.js'
import { StatusChip } from '@/components/StatusChip.js'
import { TodaysAppointments } from '@/components/TodaysAppointments.js'

/** "2026-09-05" from a local Date. */
function toDateKey(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * Dashboard showing at-a-glance metrics, a switchable status list (defaults
 * to jobcards awaiting approval, which go stale) and recent activity.
 */
export function DashboardPage() {
  const navigate = useNavigate()
  const formatters = useFormatters()
  const { t } = useTranslation()
  const { appointmentsEnabled } = useFeatures()
  const [statusFilter, setStatusFilter] = useState<DashboardStatusFilter>('awaiting_approval')
  const today = toDateKey(new Date())

  /** Same tab set as the Jobcards page, so "jump to a status" means the same thing everywhere. */
  const STATUS_TABS: { value: DashboardStatusFilter; label: string }[] = [
    { value: 'open', label: t('status.open') },
    ...JOBCARD_STATUSES.map((status) => ({ value: status, label: t(`status.${status}`, STATUS_LABELS[status]) })),
    { value: 'all', label: t('status.all') },
  ]

  const { data: summary, isLoading, error, refetch } = useQuery({
    queryKey: qk.dashboard,
    queryFn: () => api.dashboard(),
  })

  const { data: filteredJobcards, isLoading: isFilteredLoading } = useQuery({
    queryKey: qk.jobcards({ status: statusFilter, page: 1, pageSize: 10 }),
    queryFn: () => api.jobcards.list({ status: statusFilter, page: 1, pageSize: 10 }),
  })

  if (error) {
    return (
      <div className="space-y-8">
        <PageHeader
          title={t('dashboard.title')}
          actions={
            <Button
              variant="filled"
              onClick={() => navigate('/jobcards/new')}
            >
              {t('common.newJobcard')}
            </Button>
          }
        />
        <div className="flex items-center justify-center py-12">
          <EmptyState
            title={t('dashboard.failedToLoadDashboard')}
            description={t('common.pleaseTryAgain')}
            action={
              <Button variant="outlined" onClick={() => refetch()}>
                {t('common.retry')}
              </Button>
            }
          />
        </div>
      </div>
    )
  }

  // Skeleton cards for loading state
  if (isLoading || !summary) {
    return (
      <div className="space-y-8">
        <PageHeader
          title={t('dashboard.title')}
          actions={
            <Button variant="filled" disabled>
              {t('common.newJobcard')}
            </Button>
          }
        />
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="bg-surface-container border border-outline-variant rounded-lg p-5"
            >
              <Skeleton lines={3} />
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Build breakdown line for open jobcards
  const openBreakdown = t('dashboard.openBreakdown', {
    draft: summary.openJobcards.draft,
    awaiting: summary.openJobcards.awaitingApproval,
    inProgress: summary.openJobcards.inProgress,
    completed: summary.openJobcards.completed,
  })

  // Waiting for approval table columns
  const awaitingColumns: Column<(typeof summary.awaitingApproval)[number]>[] = [
    {
      key: 'number',
      header: t('dashboard.columns.number'),
      cell: (row) => <div className="ref">{row.number}</div>,
      width: 'w-32',
    },
    {
      key: 'clientName',
      header: t('dashboard.columns.client'),
      cell: (row) => (
        <div className={row.clientName ? 'text-on-surface' : 'text-on-surface-variant'}>
          {row.clientName || t('common.noCustomerYet')}
        </div>
      ),
    },
    {
      key: 'carLabel',
      header: t('dashboard.columns.car'),
      cell: (row) => <div>{row.carLabel}</div>,
    },
    {
      key: 'status',
      header: t('dashboard.columns.status'),
      cell: (row) => <StatusChip status={row.status} />,
      width: 'w-40',
    },
    {
      key: 'totalMinor',
      header: t('dashboard.columns.total'),
      cell: (row) => <div className="tabular">{formatters.money(row.totalMinor)}</div>,
      numeric: true,
      width: 'w-28',
    },
  ]

  // Recent activity table columns
  const recentColumns: Column<(typeof summary.recentJobcards)[number]>[] = [
    {
      key: 'number',
      header: t('dashboard.columns.number'),
      cell: (row) => <div className="ref">{row.number}</div>,
      width: 'w-32',
    },
    {
      key: 'clientName',
      header: t('dashboard.columns.client'),
      cell: (row) => (
        <div className={row.clientName ? 'text-on-surface' : 'text-on-surface-variant'}>
          {row.clientName || t('common.noCustomerYet')}
        </div>
      ),
    },
    {
      key: 'carLabel',
      header: t('dashboard.columns.car'),
      cell: (row) => <div>{row.carLabel}</div>,
    },
    {
      key: 'status',
      header: t('dashboard.columns.status'),
      cell: (row) => <StatusChip status={row.status} />,
      width: 'w-40',
    },
    {
      key: 'totalMinor',
      header: t('dashboard.columns.total'),
      cell: (row) => <div className="tabular">{formatters.money(row.totalMinor)}</div>,
      numeric: true,
      width: 'w-28',
    },
    {
      key: 'updatedAt',
      header: t('dashboard.columns.updated'),
      cell: (row) => <div>{formatters.date(row.updatedAt)}</div>,
      hideOnMobile: true,
      width: 'w-28',
    },
  ]

  return (
    <div className="space-y-8">
      <PageHeader
        title={t('dashboard.title')}
        actions={
          <Button
            variant="filled"
            onClick={() => navigate('/jobcards/new')}
          >
            {t('common.newJobcard')}
          </Button>
        }
      />

      {/* Stat cards grid — four cards, filling lg:grid-cols-4 cleanly */}
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label={t('dashboard.totalClients')}
          value={summary.totalClients}
          to="/clients"
        />
        <StatCard
          label={t('dashboard.totalCars')}
          value={summary.totalCars}
        />
        <StatCard
          label={t('dashboard.openJobcards')}
          value={summary.openJobcards.total}
          breakdown={openBreakdown}
          to="/jobcards?status=open"
        />
        {/*
          Links into Finance for the current month. Note the two figures measure
          different things on purpose: this card is ACCRUAL (invoices issued),
          while Finance is CASH (money actually received), so they will not
          agree for a month with unpaid invoices in it.
        */}
        <StatCard
          label={t('dashboard.invoicedThisMonth')}
          value={formatters.money(summary.invoicedThisMonthMinor)}
          meta={t('dashboard.thisMonth')}
          to="/finance?months=1"
        />
      </div>

      {/* Today's appointments section */}
      {appointmentsEnabled && summary.todaysAppointments !== null && (
        <TodaysAppointments
          appointments={summary.todaysAppointments}
          todayDate={today}
          onRefresh={() => refetch()}
        />
      )}

      {/* Switchable status section — defaults to awaiting approval, which goes stale */}
      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Segmented
            label={t('dashboard.jobcardStatus')}
            options={STATUS_TABS}
            value={statusFilter}
            onChange={setStatusFilter}
            scrollable
          />
          <Link
            to={`/jobcards?status=${statusFilter}`}
            className="text-label-lg text-on-surface-variant hover:text-on-surface transition-colors"
          >
            {t('dashboard.viewAll')}
          </Link>
        </div>
        {isFilteredLoading ? (
          <Skeleton lines={3} />
        ) : (
          <DataTable
            columns={awaitingColumns}
            rows={filteredJobcards?.items ?? []}
            rowKey={(row) => row.id}
            onRowClick={(row) => navigate(`/jobcards/${row.id}`)}
            stackOnMobile
            empty={
              <EmptyState
                title={t('dashboard.noJobcardsHere')}
                description={t('dashboard.nothingMatchesStatus')}
              />
            }
          />
        )}
      </section>

      {/* Recent activity section */}
      <section className="space-y-4">
        <h2 className="text-title-md text-on-surface">{t('dashboard.recentActivity')}</h2>
        <DataTable
          columns={recentColumns}
          rows={summary.recentJobcards}
          rowKey={(row) => row.id}
          onRowClick={(row) => navigate(`/jobcards/${row.id}`)}
          stackOnMobile
          empty={
            <EmptyState
              title={t('dashboard.noJobcardsYet')}
              description={t('dashboard.createFirstJobcard')}
              action={
                <Button
                  variant="outlined"
                  onClick={() => navigate('/jobcards/new')}
                >
                  {t('common.newJobcard')}
                </Button>
              }
            />
          }
        />
      </section>
    </div>
  )
}
